/**
 * Compact color picker: native OS color dialog + hex-text input + preset
 * swatches. Zero dependencies. Keeps the visible swatch, the free-form hex,
 * and the OS color-wheel in sync via a single `value`.
 */
export default function ColorPicker({
  value,
  onChange,
  presets = ["#1F6FEB", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0F172A", "#F59E0B", "#EC4899"],
  label,
  testIdPrefix = "color",
}) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#1F6FEB";
  const commit = (next) => {
    if (typeof next !== "string") return;
    const trimmed = next.trim();
    if (!trimmed) return;
    const prefixed = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    if (!/^#[0-9a-fA-F]{6}$/.test(prefixed)) return;
    onChange(prefixed.toLowerCase());
  };

  return (
    <div>
      {label && (
        <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{label}</label>
      )}
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        {/* OS-native color dialog — always the biggest picker surface without a dep */}
        <input
          type="color"
          value={safe}
          onChange={e => onChange(e.target.value)}
          className="h-10 w-14 rounded cursor-pointer border border-slate-200"
          data-testid={`${testIdPrefix}-native-input`}
          aria-label={label ? `${label} color wheel` : "Color wheel"}
        />
        {/* Free-form hex — power users type direct values */}
        <input
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          onBlur={e => commit(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-mono w-32 uppercase"
          placeholder="#1F6FEB"
          maxLength={7}
          data-testid={`${testIdPrefix}-hex-input`}
        />
        {/* Preset swatches — one-tap defaults for the common palette */}
        <div className="flex gap-1">
          {presets.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={`h-8 w-8 rounded-full border-2 transition-all ${
                safe.toLowerCase() === c.toLowerCase()
                  ? "border-slate-900 scale-110"
                  : "border-white ring-1 ring-slate-200 hover:scale-110"
              }`}
              style={{ background: c }}
              aria-label={`Set color to ${c}`}
              data-testid={`${testIdPrefix}-preset-${c.replace("#", "")}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
